import {
  Badge,
  FlexBox,
  Icon,
  IconButton,
  IconNames,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import {
  ButtonHTMLAttributes,
  MouseEvent,
  MouseEventHandler,
  useRef,
} from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useScrolling } from 'react-use';

import { WebsocketRequestMessage } from 'app/middleware/websocket/types/outboundWebsocketEvent';
import { RootReducerStateT } from 'app/reducers/rootReducer';
import { createSearchUrl } from 'app/views/layout/views/search/utils';
import { createAppUrl } from 'utils/url/createAppUrl';

import { TypingType } from '../../conversation/reducers/typing';
import { PresenceList } from '../../presence/components/presenceList';
import type { UserPresence } from '../../presence/reducers/presence';

import '../styles/menu.less';

import { Conversations } from './conversations';
import { MenuHeader } from './menuHeader';
import styles from './sidebarMenu.module.scss';

export type DevNoticeT = {
  title: string;
  label: string;
  onClose?: VoidFunction;
};

export type StatePropsT = {
  profile: RootReducerStateT['settings']['profile']['profile'];
  presence: RootReducerStateT['presence'];
  presenceByStatus: UserPresence[][];
  searchParams: RootReducerStateT['search']['params'];
  metaConversation: RootReducerStateT['conversation']['metaConversation'];
  metaConversations: RootReducerStateT['conversations']['metaConversations'];
  metaConversationsBeforeBreak: RootReducerStateT['conversations']['metaConversations'];
  metaConversationsAfterBreak: RootReducerStateT['conversations']['metaConversations'];
  typings: RootReducerStateT['typing']['typings'];
  conversationsLastSearchParams: RootReducerStateT['conversationsPage']['lastSearchParams'];
  isMenuOpen: RootReducerStateT['settings']['personal']['isLeftMenuOpen'];
  menuDevNotices: DevNoticeT[];
  hasChatWithDixaSupport: boolean;
  hasKnowledgeEnabled: boolean;
  hasAnalytics: boolean;
};

export type LeftMenuTogglePayloadT = { key: string; value: boolean };

export type DispatchPropsT = {
  showUserModal: () => void;
  typingRemove: (typing: TypingType) => void;
  conversationsRemove: (csid: number) => void;
  wsRequest: (payload: WebsocketRequestMessage) => Promise<unknown>;
  personalLeftMenuToggle: (payload: LeftMenuTogglePayloadT) => Promise<unknown>;
};

export type OwnPropsT = {
  isMenuAlwaysOpen?: boolean;
  onToggleNavigation?: () => void;
};

export type SidebarMenuProps = StatePropsT & DispatchPropsT & OwnPropsT;

type NavItemPropsT = {
  to: string;
  title: string;
  icon: IconNames;
  beta?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

const PageNavItem = ({
  to,
  title,
  icon,
  beta = false,
  onClick,
}: NavItemPropsT): JSX.Element => {
  const location = useLocation();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const isCtrlOrCmdPressed = event.metaKey || event.ctrlKey;
    const isSamePathname = to.split('?')[0] === location.pathname;

    // Prevent navigation if the user is already on the page. This ensures that any query parameters
    // such as filters are not lost if clicking the sidebar link while already on the page. Opening
    // the link in a new tab or window is still allowed by checking if the Ctrl/Cmd keys are pressed.
    if (isSamePathname && !isCtrlOrCmdPressed) {
      event.preventDefault();
      return;
    }

    ampli.sidebarNavClicked({ item: title });
    onClick?.(event);
  };

  return (
    <li className={styles.pageNavItem}>
      <NavLink
        className={({ isActive }) =>
          classnames(styles.pageNavBtn, { [styles.pageNavBtnActive]: isActive })
        }
        to={to}
        title={title}
        onClick={handleClick}
      >
        <Icon name={icon} size={20} className={styles.pageNavItemIcon} />
        <span className={styles.pageNavItemName}>{title}</span>

        {beta && (
          <Badge
            variant="primary"
            size="small"
            className={styles.pageNavItemBadge}
          >
            Beta
          </Badge>
        )}
      </NavLink>
    </li>
  );
};

const FooterItem = ({
  icon,
  className,
  children,
  ...buttonProps
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: IconNames;
}): JSX.Element => (
  <li>
    <button
      className={classnames(styles.sidebarFooterButton, className)}
      {...buttonProps}
    >
      <Icon name={icon} size={20} className={styles.pageNavItemIcon} />
      <span className={styles.pageNavItemName}>{children}</span>
    </button>
  </li>
);

export const SidebarMenu = (props: SidebarMenuProps): JSX.Element => {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrolling = useScrolling(rootRef);

  const toggleMenu = () => {
    props.personalLeftMenuToggle({
      key: 'isLeftMenuOpen',
      value: !props.isMenuOpen,
    });

    props.isMenuOpen ? ampli.sidebarCollapsed() : ampli.sidebarExpanded();
  };

  const getSearchLink = () => {
    return createSearchUrl('conversations', props.searchParams || {});
  };

  const openDixaMessenger = (): void => {
    if (props.hasChatWithDixaSupport) {
      window._dixa_?.invoke('setWidgetOpen', true);
    }
  };

  const shouldShowFooter = (): boolean => {
    const { isMenuOpen, isMenuAlwaysOpen, hasChatWithDixaSupport } = props;
    return hasChatWithDixaSupport || isMenuAlwaysOpen || !isMenuOpen;
  };

  const handleAddAgentButtonClick = (): void => {
    ampli.sidebarAddAgentButtonClicked();
    props.showUserModal();
  };

  const {
    profile,
    presenceByStatus,
    presence,
    isMenuOpen,
    isMenuAlwaysOpen,
    menuDevNotices,
    hasChatWithDixaSupport,
    hasKnowledgeEnabled,
    hasAnalytics,
    conversationsLastSearchParams,
  } = props;

  const hasInviteAccess = useHasAccess('organization.invite', {
    fallbackRole: 'admin',
  });

  const rootClass = classnames({
    [styles.root]: true,
    [styles.rootCollapsed]: !isMenuAlwaysOpen && !isMenuOpen,
  });

  const conversationsPath = createAppUrl({
    path: '/conversations',
    params: { search: conversationsLastSearchParams },
  });

  return (
    <div ref={rootRef} className={rootClass}>
      <nav className={styles.sidebar} role="navigation" id="main-sidebar">
        <ul className={styles.headWrapper}>
          <li>
            {!isMenuAlwaysOpen ? (
              <a
                onClick={toggleMenu}
                title="Toggle menu"
                className={styles.toggleMenuContainer}
              >
                <Icon
                  size={24}
                  name={isMenuOpen ? 'angle-double-left' : 'angle-double-right'}
                  className={styles.toggleMenu}
                />
              </a>
            ) : (
              <a
                onClick={props.onToggleNavigation}
                title="Toggle menu"
                className={styles.toggleMenuContainer}
                style={{ opacity: '1' }}
              >
                <Icon size={24} name="close" className={styles.toggleMenu} />
              </a>
            )}
          </li>
          <li>
            <Link
              to="/"
              title="Dashboard"
              className={styles.logoContainer}
              onClick={() => {
                props.onToggleNavigation?.();
                ampli.sidebarNavClicked({ item: 'Logo' });
              }}
            >
              <Icon size={20} name="dixa" className={styles.logo} />
            </Link>
          </li>
        </ul>

        {menuDevNotices.map((notice: DevNoticeT, index: number) => (
          <Tooltip key={`menu-notice-${index}`} content={notice.label} dark>
            <div className={styles.devNotice} title={notice.title}>
              {notice.label}

              {notice.onClose && (
                <IconButton
                  icon="close"
                  variant="danger"
                  className={styles.devNoticeClose}
                  onClick={notice.onClose}
                />
              )}
            </div>
          </Tooltip>
        ))}

        <ul className={styles.pageNav}>
          <PageNavItem
            to="/dashboard"
            title="Dashboard"
            icon="dashboard"
            onClick={props.onToggleNavigation}
          />
          <PageNavItem
            to={conversationsPath}
            title="Conversations"
            icon="conversations"
            onClick={props.onToggleNavigation}
          />
          <PageNavItem
            to={'/contacts'}
            title="Contacts"
            icon="id-card"
            onClick={props.onToggleNavigation}
          />
          <PageNavItem
            to={getSearchLink()}
            title="Search"
            icon="search"
            onClick={props.onToggleNavigation}
          />

          <PageNavItem
            to="/team"
            title="Team overview"
            icon="team"
            onClick={props.onToggleNavigation}
          />

          {hasAnalytics && (
            <PageNavItem
              to="/analytics"
              title="Analytics"
              icon="chart-bar"
              onClick={props.onToggleNavigation}
            />
          )}

          {hasKnowledgeEnabled && (
            <PageNavItem
              to="/knowledge"
              title="Knowledge"
              icon="file-text"
              onClick={props.onToggleNavigation}
            />
          )}
          {hasKnowledgeEnabled && (
            <PageNavItem
              to="/internal-kb"
              title="Internal Knowledge Base"
              icon="file-text-search"
              onClick={props.onToggleNavigation}
            />
          )}
          <PageNavItem
            to="/settings"
            title="Settings"
            icon="cog"
            onClick={props.onToggleNavigation}
          />
        </ul>

        <div>
          <MenuHeader
            title="Conversations"
            isMenuOpen={isMenuAlwaysOpen || isMenuOpen}
          />
          <Conversations {...props} />
        </div>

        {(isMenuAlwaysOpen || isMenuOpen) && (
          <div className={styles.presenceList}>
            <FlexBox alignItems="center" justifyContent="space-between">
              <MenuHeader
                title="Team"
                isMenuOpen={isMenuAlwaysOpen || isMenuOpen}
              />
              {hasInviteAccess && (
                <Tooltip content="Add new agent" dark>
                  <a
                    className={styles.newUserButton}
                    onClick={handleAddAgentButtonClick}
                    role="button"
                  >
                    <Icon name="plus" size={20} />
                  </a>
                </Tooltip>
              )}
            </FlexBox>

            <PresenceList
              presenceByStatus={presenceByStatus}
              presence={presence}
              profile={profile}
              scroll={scrolling}
            />
          </div>
        )}

        {shouldShowFooter() && (
          <ul className={styles.sidebarFooter}>
            {!isMenuOpen && !isMenuAlwaysOpen && (
              <FooterItem icon="users" onClick={toggleMenu} />
            )}

            {hasChatWithDixaSupport && (
              <FooterItem icon="chat" onClick={openDixaMessenger}>
                Chat with support
              </FooterItem>
            )}
          </ul>
        )}
      </nav>
    </div>
  );
};
